<template>
  <div>
    <AuBreadcrumb
      :pages="breadcrumb"
      :current-page-title="isAddNewsMode ? $t('router.pages.createNews') : ''"
    />
    <Form
      v-if="showNewsTable"
      :is-bordered="false"
    >
      <template #title>
        {{ $t('news.label') }}
      </template>
      <template #subtitle>
        <div class="items-count">
          <span>
            {{ $t("news.newsCount") }}:
          </span>
          <AuSkeleton v-if="dataLoading" />
          <span v-else>
            {{ blogPagesCount }}
          </span>
        </div>
      </template>

      <template #header-right>
        <AuButton
          v-if="isIssuer"
          type="primary"
          @click="createNews"
        >
          <span class="bnt-text">{{ $t("news.addNewsButton") }}</span>
        </AuButton>
      </template>

      <ProgressLoader
        v-if="dataLoading"
        mode="indeterminate"
        color="#5AC396"
        size="150px"
      />

      <div v-else>
        <div v-if="haveNews">
          <!-- Заменяем AuTable на UTable -->
          <UTable
            :items="formattedNews"
            :columns="tableColumns"
          >
            <!-- Слот для изображения -->
            <template #image="{ value }">
              <img
                class="image"
                :src="value"
                alt=""
              >
            </template>

            <!-- Слот для заголовка -->
            <template #title="{ value }">
              {{ value }}
            </template>

            <!-- Слот для статуса -->
            <template #isDraft="{ value }">
              {{ value }}
            </template>

            <!-- Слот для действий - пустой, так как используется type="menu" -->
            <template #actions>
              <!-- Пустой слот, рендеринг через menuFunction -->
            </template>
          </UTable>

          <AuPagination
            v-if="blogPagesCount > 30"
            :total-items="blogPagesCount"
            @pageChanged="pageChanged"
          />
        </div>
      </div>
    </Form>

    <RouterView />

    <!-- Модальное окно подтверждения удаления -->
    <AuModal
      v-model="deleteDialog"
      width="500px"
    >
      <template #header>
        <span class="modal_head_text">
          {{ $t("news.deleteDialog.title") }}
        </span>
      </template>
      <template #body>
        <span>
          {{ $t("news.deleteDialog.subtitle") }}
        </span>
      </template>
      <template #footer>
        <div class="modal_button">
          <div class="modal_button_margin">
            <AuButton
              bordered
              center
              width="80px"
              @click="modalClick"
            >
              {{ $t("news.deleteDialog.agree") }}
            </AuButton>
          </div>
          <div class="modal_button_margin">
            <AuButton
              bordered
              center
              width="80px"
              @click="delCancel"
            >
              {{ $t("news.deleteDialog.cancel") }}
            </AuButton>
          </div>
        </div>
      </template>
    </AuModal>
  </div>
</template>

<script>
import _ from "lodash";
import isUserRole from "@/help/isUserRole";
import ProgressLoader from "@/loaders/progress-bar";
import UTable from "@/components/uikit/uComponents/UTable.vue";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "News",
  components: {
    ProgressLoader,
    UTable
  },
  data() {
    return {
      deleteDialog: false,
      deleteId: null,
      totalItems: 40
    };
  },

  computed: {
    ...mapGetters({
      blogPages: "news/blogPages",
      blogPagesCount: "news/blogPagesCount",
      dataLoading: "news/dataLoading"
    }),

    breadcrumb() {
      const ret = [{ key: 0, name: this.$t("router.pages.dashboard"), path: "/" }];
      if (!this.showNewsTable) {
        return [...ret, { key: 1, name: this.$t("router.pages.news"), path: "/news" }];
      }
      return ret;
    },

    haveNews() {
      return !_.isEmpty(this.blogPages);
    },

    // Колонки для UTable
    tableColumns() {
      return [
        {
          property: "image",
          header: this.$t("news.firstColumn"),
          align: "start"
        },
        {
          property: "title",
          header: this.$t("news.secondColumn"),
          align: "start"
        },
        {
          property: "isDraft",
          header: this.$t("news.thirdColumn"),
          align: "center"
        },
        {
          property: "actions",
          header: this.$t("news.fourthColumn"),
          align: "center",
          type: "menu",
          menuFunction: this.getNewsMenuItems
        }
      ];
    },

    // Форматирование данных для таблицы
    formattedNews() {
      return _.map(this.blogPages, (blogPage, i) => {
        // Создаем уникальный ID
        const newsId = blogPage.id || `news-${i}`;

        return {
          id: newsId,
          image: blogPage.imageFile,
          title: blogPage.title || this.$t("news.responseTable.title"),
          isDraft: blogPage.isDraft ? this.$t("news.responseTable.statusDraft") : this.$t("news.responseTable.statusPublished"),
          // Данные для контекстного меню
          actionData: {
            id: blogPage.id,
            index: i
          }
        };
      });
    },

    showNewsTable() {
      return !_.includes(this.$route.path, "edit-news");
    },

    isAddNewsMode() {
      return this.$route.params?.id === "new";
    },

    isIssuer() {
      return isUserRole() === "issuer";
    }
  },

  async created() {
    await this.getBlogPages();
  },

  async beforeDestroy() {
    await this.setBlogPagesPage(1);
  },

  methods: {
    ...mapActions({
      removeBlogPage: "news/removeBlogPage",
      setBlogPagesPage: "news/setBlogPagesPage",
      getBlogPages: "news/getBlogPages"
    }),

    // Функция для генерации пунктов контекстного меню
    getNewsMenuItems(actionData) {
      return [
        {
          items: [
            {
              label: this.$t("counterparties.editAction"),
              command: () => {
                this.editNews(actionData.id);
              }
            },
            {
              label: this.$t("counterparties.deleteAction"),
              command: () => {
                this.showDeleteDialog(actionData.id);
              }
            }
          ]
        }
      ];
    },

    async pageChanged(page) {
      this.setBlogPagesPage(page);
      await this.getBlogPages();
    },

    delCancel() {
      this.deleteDialog = false;
      this.deleteId = null;
    },

    async modalClick() {
      try {
        await this.removeBlogPage(this.deleteId);
        this.deleteId = null;
        this.deleteDialog = false;
        this.$toast.success(this.$t("toasts.success.newsDelete"));
        await this.getBlogPages();
      }
      catch (error) {
        this.$toast.error(this.$t("toasts.error.newsDelete"));
      }
    },

    showDeleteDialog(id) {
      this.deleteDialog = true;
      this.deleteId = id;
    },

    createNews() {
      this.$router.push({ name: "edit news", params: { id: "new" } });
    },

    editNews(id) {
      this.$router.push({ name: "edit news", params: { id: id } });
    }
  }
};
</script>

<style scoped lang="scss">
.projects{
  padding: 20px;
  font-family: 'Suisse Intl',serif;
  font-style: normal;
}
.header {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
}
.title{
  font-size: 24px;
  line-height: 1.1em;
}
.count{
  font-weight: 500;
  font-size: 14px;
  color: var(--color-60);
}
.bnt-text{
  font-weight: 450;
  font-size: 12px;
}
.p-indent{
  margin-bottom: 30px;
}
.modal_head_text {
  font-family: 'Suisse Intl',serif;
  font-style: normal;
  font-weight: 450;
  font-size: 20px;
  margin-bottom: 20px;
}
.btn_indent{
  display: flex;
  justify-content: end;
  margin-bottom: 15px;
}
.modal_button {
  display: flex;
  justify-content: end;
  margin-top: 15px;
}
.modal_button_margin {
  margin-right: 10px;
}
.btn-text{
  font-weight: 450;
  font-size: 12px;
}
.image {
  width: 80px;
  height: 80px;
  margin-right: 8px;
  border-radius: 7px;
  padding: 4px;
  object-fit: cover;
}
.items-count {
  display: flex;
  align-items: center;
  gap: 5px;

  span {
    font-weight: 450;
    font-size: 12px;
    color: var(--color-60);
  }
}
</style>